import ResponsiveImage from "../responsive-image/ResponsiveImage";
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import "./buy&rent.css";
import "./PillButton.css";
import ShopWithFilters from "../shop/ShopWithFilters";
import InstrumentInvitation from "../shop/InstrumentInvitation";
import features from "../../config/features.json";
import Rent2 from "./rent2.jpg";
import WoodenBG from "./Ahorn2 5.jpeg";
import ViolinImage from "./violin.jpeg";
import ViolaImage from "./viola.jpeg";
import CelloImage from "./cello.jpeg";
import BassImage from "./bass.jpeg";
import enTranslations from "./en.json";
import deTranslations from "./de.json";

const BuyAndRent = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const [showCollapsedText, setShowCollapsedText] = useState(true);
  const [dimensions, setDimensions] = useState({
    overlayWidth: "40vw",
    fontSize: "38px",
    titleSize: "64px",
    traditionTopSize: "52px",
    traditionBottomSize: "52px",
    isMobile: false,
    isTablet: false,
  });

  i18n.addResourceBundle("en", "buyrent", enTranslations, true, true);
  i18n.addResourceBundle("de", "buyrent", deTranslations, true, true);

  useEffect(() => {
    const updateResponsiveDimensions = () => {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      const aspectRatio = viewportWidth / viewportHeight;

      // Mobile breakpoint
      if (viewportWidth <= 768) {
        setDimensions({
          overlayWidth: "100%",
          fontSize: "16px",
          titleSize: "28px",
          traditionTopSize: "32px",
          traditionBottomSize: "32px",
          isMobile: true,
          isTablet: false,
        });
        return;
      }

      // Tablet breakpoint
      if (viewportWidth <= 1200) {
        const tabletRatio = (viewportWidth - 768) / (1200 - 768);
        setDimensions({
          overlayWidth: "100%",
          fontSize: `${16 + tabletRatio * 14}px`, // 16px -> 30px
          titleSize: `${28 + tabletRatio * 12}px`, // 28px -> 40px
          traditionTopSize: `${32 + tabletRatio * 12}px`, // 32px -> 44px
          traditionBottomSize: `${32 + tabletRatio * 12}px`, // 32px -> 44px
          isMobile: false,
          isTablet: true,
        });
        return;
      }

      // Desktop - smooth calculations
      const minAspectRatio = 1.0;
      const maxAspectRatio = 2.0;
      const veryWideAspectRatio = 2.8;

      const clampedRatio = Math.max(
        minAspectRatio,
        Math.min(aspectRatio, veryWideAspectRatio),
      );
      const normalizedRatio =
        (clampedRatio - minAspectRatio) / (maxAspectRatio - minAspectRatio);
      const clampedNormalized = Math.max(0, Math.min(1, normalizedRatio));

      // Wide screen factor
      let wideScreenFactor = 1;
      if (aspectRatio > maxAspectRatio) {
        wideScreenFactor =
          1 -
          ((aspectRatio - maxAspectRatio) /
            (veryWideAspectRatio - maxAspectRatio)) *
            0.15;
        wideScreenFactor = Math.max(0.85, wideScreenFactor);
      }

      // Smooth interpolation for overlay width
      const minOverlayWidth = 45;
      const maxOverlayWidth = 35;
      const overlayWidthValue =
        (minOverlayWidth +
          (maxOverlayWidth - minOverlayWidth) * clampedNormalized) *
        wideScreenFactor;

      // Font sizes
      const minFontSize = 32;
      const maxFontSize = 38;
      const fontSize =
        (minFontSize + (maxFontSize - minFontSize) * clampedNormalized) *
        wideScreenFactor;

      const minTitleSize = 52;
      const maxTitleSize = 64;
      const titleSize =
        (minTitleSize + (maxTitleSize - minTitleSize) * clampedNormalized) *
        wideScreenFactor;

      const minTraditionSize = 44;
      const maxTraditionSize = 52;
      const traditionSize =
        (minTraditionSize +
          (maxTraditionSize - minTraditionSize) * clampedNormalized) *
        wideScreenFactor;

      setDimensions({
        overlayWidth: `${overlayWidthValue}vw`,
        fontSize: `${fontSize}px`,
        titleSize: `${titleSize}px`,
        traditionTopSize: `${traditionSize}px`,
        traditionBottomSize: `${traditionSize}px`,
        isMobile: false,
        isTablet: false,
      });
    };

    updateResponsiveDimensions();
    window.addEventListener("resize", updateResponsiveDimensions);
    return () =>
      window.removeEventListener("resize", updateResponsiveDimensions);
  }, []);

  const handleExpandOverlay = () => {
    setShowCollapsedText(false);
    setIsExpanded(true);
  };

  const handleCollapseOverlay = () => {
    setIsExpanded(false);
    if (window.innerWidth <= 1200) {
      setShowCollapsedText(true);
    } else {
      setShowCollapsedText(false);
    }
  };

  const handleOverlayTransitionEnd = (e) => {
    if (e.propertyName === "width" && !isExpanded) {
      setShowCollapsedText(true);
    }
  };

  const handleProductClick = () => {
    navigate("/contact");
  };

  const products = [
    {
      id: 1,
      name: t("buyrent:violin_set"),
      description: t("buyrent:violin_description"),
      price: t("buyrent:price_18"),
      image: ViolinImage,
    },
    {
      id: 2,
      name: t("buyrent:viola_set"),
      description: t("buyrent:viola_description"),
      price: t("buyrent:price_20"),
      image: ViolaImage,
    },
    {
      id: 3,
      name: t("buyrent:cello_set"),
      description: t("buyrent:cello_description"),
      price: t("buyrent:price_28"),
      image: CelloImage,
    },
    {
      id: 4,
      name: t("buyrent:bass_set"),
      description: t("buyrent:bass_description"),
      price: t("buyrent:price_42"),
      image: BassImage,
    },
  ];

  return (
    <main className="buyrent-page" role="main">
      <section className="rent-section" aria-labelledby="rent-main-heading">
        <div className="rent-background">
          <ResponsiveImage sizes="100vw"
            src={Rent2}
            loading="eager"
            fetchpriority="high"
            alt={t("buyrent:rent_background_alt")}
            className="rent-bg-image"
            role="img"
            aria-label={t("buyrent:rent_background_description")}
          />
        </div>

        <div
          className={`overlay-container ${isExpanded ? "expanded" : ""}`}
          style={{
            width:
              dimensions.isMobile || dimensions.isTablet || isExpanded
                ? "100%"
                : dimensions.overlayWidth,
          }}
          onTransitionEnd={handleOverlayTransitionEnd}
          role="region"
          aria-label={t("buyrent:rental_info_region")}
        >
          {!isExpanded ? (
            <div className="overlay-collapsed">
              {showCollapsedText && (
                <>
                  <div
                    className="overlay-text"
                    style={{ fontSize: dimensions.fontSize }}
                  >
                    <p>{t("buyrent:rent_overlay_text")}</p>
                  </div>
                  <button
                    className="buyrent-pill-btn focus-highlight"
                    onClick={handleExpandOverlay}
                    tabIndex={0}
                    aria-label={t("buyrent:expand_rental_info_aria_label")}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleExpandOverlay();
                      }
                    }}
                  >
                    {t("buyrent:expand_label")}
                  </button>
                </>
              )}
              {!showCollapsedText && (
                <div
                  style={{ width: 0, height: 0, overflow: "hidden" }}
                  aria-hidden="true"
                />
              )}
            </div>
          ) : (
            <div className="overlay-expanded">
              <div className="overlay-header">
                <h2
                  id="rent-main-heading"
                  className="overlay-title"
                  style={{ fontSize: dimensions.titleSize }}
                >
                  {t("buyrent:for_rent")}
                </h2>
                <button
                  className="buyrent-pill-btn focus-highlight"
                  onClick={handleCollapseOverlay}
                  tabIndex={0}
                  aria-label={t("buyrent:collapse_rental_info_aria_label")}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleCollapseOverlay();
                    }
                  }}
                >
                  {t("buyrent:collapse_label")}
                </button>
              </div>

              <div
                className="products-grid"
                role="grid"
                aria-label={t("buyrent:rental_products_grid")}
              >
                {products.map((product) => (
                  <div
                    key={product.id}
                    className="product-card focus-highlight"
                    onClick={handleProductClick}
                    tabIndex={0}
                    role="button"
                    aria-label={t("buyrent:product_card_aria_label", {
                      name: product.name,
                      price: product.price,
                    })}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault();
                        handleProductClick();
                      }
                    }}
                  >
                    <div className="product-image">
                      <ResponsiveImage sizes="(max-width: 1200px) 300px, 25vw"
                        src={product.image}
                        alt={t("buyrent:product_image_alt", {
                          name: product.name,
                        })}
                        style={{
                          width: "100%",
                          height: "100%",
                          objectFit: "cover",
                        }}
                      />
                    </div>
                    <div className="product-info">
                      <h3 className="product-name">{product.name}</h3>
                      <p className="product-description">
                        {product.description}
                      </p>
                      <p className="product-price">{product.price}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>

      <section
        className="buyrent-intro-section"
        role="region"
        aria-label={t("buyrent:buying_intro_aria_label")}
      >
        <div className="buyrent-intro-content">
          <p>{t("buyrent:buying_intro_text")}</p>
        </div>
      </section>

      <section
        className="tradition-section focus-highlight"
        role="region"
        aria-labelledby="tradition-heading"
        tabIndex={0}
      >
        <div className="tradition-background">
          <ResponsiveImage sizes="100vw"
            src={WoodenBG}
            alt="Wooden Background"
            className="tradition-bg-image"
          />
        </div>
        <div className="tradition-content-overlay">
          <div
            className="tradition-text-top-left focus-highlight"
            tabIndex={0}
            role="text"
            aria-label={`${t("buyrent:precision")} ${t("buyrent:passion")} ${t(
              "buyrent:performance",
            )}`}
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            <h2
              id="tradition-heading"
              style={{ fontSize: dimensions.traditionTopSize }}
            >
              {t("buyrent:precision")}
              <br />
              {t("buyrent:passion")}
              <br />
              {t("buyrent:performance")}
            </h2>
          </div>
          <div
            className="tradition-text-bottom-right focus-highlight"
            tabIndex={0}
            role="text"
            aria-label={`${t("buyrent:since_1997")} ${t(
              "buyrent:making_music",
            )}`}
            style={{ pointerEvents: "none" }}
            onFocus={(e) => {
              e.target.style.pointerEvents = "auto";
            }}
            onBlur={(e) => {
              e.target.style.pointerEvents = "none";
            }}
          >
            <p style={{ fontSize: dimensions.traditionBottomSize }}>
              {t("buyrent:since_1997")}
              <br />
              {t("buyrent:making_music")}
            </p>
          </div>
        </div>
      </section>

      {features.shopEnabled ? (
        <section className="shop-section-wrapper">
          <ShopWithFilters />
        </section>
      ) : (
        <InstrumentInvitation />
      )}
    </main>
  );
};

export default BuyAndRent;
